import { InfoCircleOutlined } from '@ant-design/icons'
import { Button, Form } from 'antd'
import React, { FunctionComponent, useCallback, useState } from 'react'

import { PROPERTY_TYPES } from '../../../common/drmTypes'
import { postServiceFormHelp as _help } from '../../utils/cuzkMonitorRcFormHelp'
import { itemHelp } from '../../utils/formHelpers'

export interface PostServiceAddRecipientFormApplyPropertyAddressButtonProps {
  showHelp: boolean
}

const parsePropertyAddress = (propertyAddress?: string) => {
  if (!propertyAddress) return {}
  let streetName: string | undefined
  let houseNumber: string | undefined
  let orientationNumber: string | undefined
  let zipCode: string | undefined

  const parts = propertyAddress.split(',').map(p => p.trim())
  const lastPart = parts[parts.length - 1]
  const zipMatch = lastPart?.replace(/\s+/g, '').match(/^(\d{5})$/)
  if (zipMatch) {
    zipCode = zipMatch[1]?.slice(0, 3) + zipMatch[1]?.slice(3)
  } else {
    const anyZip = propertyAddress.match(/(\d{3})\s?(\d{2})/)
    if (anyZip) zipCode = anyZip[1] + anyZip[2]
  }

  const streetSegment = parts.find(p => /\d/.test(p)) || parts[0]
  if (streetSegment) {
    const addrMatch = streetSegment.match(
      /^(.*?)(?:\s+(\d+))(?:\/(\d+))?(?:\s|$)/
    )
    if (addrMatch) {
      streetName = addrMatch[1]?.trim()
      houseNumber = addrMatch[2]
      orientationNumber = addrMatch[3]
    } else {
      streetName = streetSegment
    }
  }
  return { streetName, houseNumber, orientationNumber, zipCode }
}

export const PostServiceAddRecipientFormApplyPropertyAddressButton: FunctionComponent<PostServiceAddRecipientFormApplyPropertyAddressButtonProps> =
  ({ showHelp }) => {
    const form = Form.useFormInstance()
    const propertyType = Form.useWatch('propertyType', form)
    const propertyCity = Form.useWatch('propertyCity', form)
    const propertyAddress = Form.useWatch('propertyAddress', form)
    const [showCopyNotice, setShowCopyNotice] = useState(false)

    const handleApply = useCallback(() => {
      if (!propertyCity && !propertyAddress) return
      const { streetName, houseNumber, orientationNumber, zipCode } =
        parsePropertyAddress(propertyAddress)

      form.setFieldsValue({
        street: {
          streetName:
            streetName || form.getFieldValue(['street', 'streetName']) || '',
          houseNumber:
            houseNumber || form.getFieldValue(['street', 'houseNumber']) || '',
          orientationNumber:
            orientationNumber ||
            form.getFieldValue(['street', 'orientationNumber']) ||
            undefined,
        },
        city: propertyCity || form.getFieldValue('city'),
        zipCode: zipCode || form.getFieldValue('zipCode'),
      })

      setShowCopyNotice(true)
    }, [form, propertyAddress, propertyCity])

    if (
      !propertyType ||
      ![PROPERTY_TYPES.BUILDING, PROPERTY_TYPES.UNIT].includes(propertyType)
    )
      return null

    return (
      <Form.Item
        label
        className="!-mt-2"
        colon={false}
        help={itemHelp(showHelp, _help.copyAddress)}
      >
        <div className="flex flex-col gap-1 items-start">
          <Button
            type="link"
            size="small"
            onClick={handleApply}
            disabled={!propertyCity && !propertyAddress}
          >
            Použít jako korespondenční adresu
          </Button>
          {showCopyNotice && (
            <div className="text-gray-500">
              <InfoCircleOutlined className="mr-1" /> Údaje byly zkopírovány.
              Zkontrolujte jejich správnost před uložením.
            </div>
          )}
        </div>
      </Form.Item>
    )
  }
